import styles from './ui.module.css';
import ReactDOM from 'react-dom';

/* ── Badge ─────────────────────────────────────────── */
export const Badge = ({ variant = 'gray', children, dot = true, size = 'md' }) => (
  <span className={`${styles.badge} ${styles[`badge_${variant}`]} ${styles[`badge_${size}`]}`}>
    {dot && <span className={styles.badge_dot} />}
    {children}
  </span>
);

/* ── Button ────────────────────────────────────────── */
export const Button = ({ variant = 'outline', size = 'md', children, loading, icon, className = '', ...props }) => (
  <button
    className={`${styles.btn} ${styles[`btn_${variant}`]} ${styles[`btn_${size}`]} ${className}`}
    disabled={loading || props.disabled}
    {...props}
  >
    {loading ? <span className={styles.spinner} /> : icon && <span>{icon}</span>}
    {children}
  </button>
);

/* ── Card ──────────────────────────────────────────── */
export const Card = ({ children, className = '', ...props }) => (
  <div className={`${styles.card} ${className}`} {...props}>{children}</div>
);
export const CardHeader = ({ title, subtitle, actions, children }) => (
  <div className={styles.card_header}>
    <div>
      {title && <div className={styles.card_title}>{title}</div>}
      {subtitle && <div className={styles.card_subtitle}>{subtitle}</div>}
      {children}
    </div>
    {actions && <div className={styles.card_actions}>{actions}</div>}
  </div>
);
export const CardBody = ({ children, noPad }) => (
  <div className={noPad ? '' : styles.card_body}>{children}</div>
);

/* ── KPI Card ──────────────────────────────────────── */
export const KpiCard = ({ label, value, sub, trend, trendDir, icon, color = 'blue' }) => (
  <div className={`${styles.kpi_card} ${styles[`kpi_${color}`]}`}>
    {icon && <div className={styles.kpi_icon}>{icon}</div>}
    <div className={styles.kpi_label}>{label}</div>
    <div className={styles.kpi_value}>{value}</div>
    {(sub || trend) && (
      <div className={styles.kpi_sub}>
        {trend && <span className={`${styles.kpi_trend} ${trendDir === 'down' ? styles.kpi_trend_down : styles.kpi_trend_up}`}>{trend}</span>}
        {sub && <span>{sub}</span>}
      </div>
    )}
  </div>
);

/* ── Input ─────────────────────────────────────────── */
export const Input = ({ label, error, className = '', ...props }) => (
  <div className={`${styles.form_group} ${className}`}>
    {label && <label className={styles.form_label}>{label}</label>}
    <input className={`${styles.form_control} ${error ? styles.form_control_error : ''}`} {...props} />
    {error && <span className={styles.form_error}>{error}</span>}
  </div>
);

/* ── Select ────────────────────────────────────────── */
export const Select = ({ label, error, children, className = '', ...props }) => (
  <div className={`${styles.form_group} ${className}`}>
    {label && <label className={styles.form_label}>{label}</label>}
    <select className={`${styles.form_select} ${error ? styles.form_control_error : ''}`} {...props}>
      {children}
    </select>
    {error && <span className={styles.form_error}>{error}</span>}
  </div>
);

/* ── Textarea ──────────────────────────────────────── */
export const Textarea = ({ label, error, className = '', ...props }) => (
  <div className={`${styles.form_group} ${className}`}>
    {label && <label className={styles.form_label}>{label}</label>}
    <textarea className={`${styles.form_control} ${error ? styles.form_control_error : ''}`} rows={3} {...props} />
    {error && <span className={styles.form_error}>{error}</span>}
  </div>
);

/* ── Table ─────────────────────────────────────────── */
export const Table = ({ columns, data, onRowClick, emptyMsg = 'No records found', loading }) => {
  if (loading) return (
    <div className={styles.table_loading}>
      <div className={styles.spinner} /> Loading…
    </div>
  );
  return (
    <div className={styles.table_wrap}>
      <table className={styles.table}>
        <thead>
          <tr>{columns.map(c => <th key={c.key || c.label}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {!data?.length
            ? <tr><td colSpan={columns.length} className={styles.table_empty}>{emptyMsg}</td></tr>
            : data.map((row, i) => (
                <tr key={row.id || i} onClick={() => onRowClick?.(row)} className={onRowClick ? styles.row_clickable : ''}>
                  {columns.map(c => (
                    <td key={c.key || c.label} className={c.mono ? styles.cell_mono : c.primary ? styles.cell_primary : ''}>
                      {c.render ? c.render(row) : row[c.key]}
                    </td>
                  ))}
                </tr>
              ))
          }
        </tbody>
      </table>
    </div>
  );
};

/* ── Modal ─────────────────────────────────────────── */
export const Modal = ({ open, onClose, title, children, size = 'md', footer }) => {
  if (!open) return null;
  const content = (
    <div className={styles.modal_overlay} tabIndex={-1}>
      <div className={`${styles.modal} ${styles[`modal_${size}`]}`} onClick={e => e.stopPropagation()}>
        <div className={styles.modal_header}>
          <span className={styles.modal_title}>{title}</span>
          <button className={styles.modal_close} onClick={onClose}>✕</button>
        </div>
        <div className={styles.modal_body}>{children}</div>
        {footer && <div className={styles.modal_footer}>{footer}</div>}
      </div>
    </div>
  );
  return ReactDOM.createPortal(content, document.body);
};

/* ── Page Header ───────────────────────────────────── */
export const PageHeader = ({ title, subtitle, actions }) => (
  <div className={styles.page_header}>
    <div>
      <h1 className={styles.page_title}>{title}</h1>
      {subtitle && <p className={styles.page_subtitle}>{subtitle}</p>}
    </div>
    {actions && <div className={styles.page_actions}>{actions}</div>}
  </div>
);

/* ── Empty State ───────────────────────────────────── */
export const Empty = ({ icon = '▣', title, description, action }) => (
  <div className={styles.empty}>
    <div className={styles.empty_icon}>{icon}</div>
    <div className={styles.empty_title}>{title}</div>
    {description && <div className={styles.empty_desc}>{description}</div>}
    {action}
  </div>
);

/* ── Status badge helpers ──────────────────────────── */
export const statusBadge = {
  asset: (s) => {
    const m = { operational:'green', maintenance:'amber', breakdown:'red', retired:'gray', disposed:'gray' };
    return <Badge variant={m[s] || 'gray'}>{s?.charAt(0).toUpperCase() + s?.slice(1)}</Badge>;
  },
  wo: (s) => {
    const m = { open:'amber', assigned:'blue', in_progress:'teal', on_hold:'purple', closed:'green', cancelled:'gray' };
    return <Badge variant={m[s] || 'gray'}>{s?.replace(/_/g,' ')?.replace(/\b\w/g,c=>c.toUpperCase())}</Badge>;
  },
  priority: (p) => {
    const m = { critical:'red', high:'amber', medium:'blue', low:'gray' };
    return <Badge variant={m[p] || 'gray'} dot={false}>{p?.charAt(0).toUpperCase() + p?.slice(1)}</Badge>;
  },
  woType: (t) => {
    const m = { pm:'blue', breakdown:'red', inspection:'teal', repair:'amber', calibration:'purple' };
    return <Badge variant={m[t] || 'gray'} dot={false}>{t?.toUpperCase()}</Badge>;
  },
  compliance: (s) => {
    const m = { valid:'green', expiring_soon:'amber', expired:'red' };
    return <Badge variant={m[s] || 'gray'}>{s?.replace(/_/g,' ')?.replace(/\b\w/g,c=>c.toUpperCase())}</Badge>;
  },
};
